import { clientApi, logout, refreshClient } from "@/utils/api";
import { COOKIE_OPTIONS, setToken } from "@/utils/token";

import { TLoginReqDto, TLoginResDto } from "@/api/services/main/auth/dtos/login.dto";
import Cookies from "js-cookie";
import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/main/auth/dtos/refresh-token.dto";
import { TResetPasswordReqDto, TResetPasswordResDto } from "@/api/services/main/auth/dtos/reset-password.dto";
import { TSendEmailToChangeUserEmailReqDto, TSendEmailToChangeUserEmailResDto } from "@/api/services/main/auth/dtos/send-email-to-change-email.dto";
import { TUpdateUserEmailReqDto, TUpdateUserEmailResDto } from "@/api/services/main/auth/dtos/update-user-email.dto";
import { TSetPasswordReqDto, TSetPasswordResDto } from "@/api/services/main/auth/dtos/set-password.dto";
import { TLogoutResDto } from "@/api/services/main/auth/dtos/logout.dto";
import { TValidateNewEmailReqDto, TValidateNewEmailResDto } from "@/api/services/main/auth/dtos/validate-new-email-exists.dto";
import { TValidateTokenReqDto, TValidateTokenResDto, TValidateTokenRes } from "@/api/services/main/auth/dtos/validate-token.dto";
import { TCheckWebAccessReqDto, TCheckWebAccessRes, TCheckWebAccessResDto } from "@/api/services/main/auth/dtos/check-web-access.dto";
import { PRIVACY_CONSENT_APPROVED_KEY, PRIVACY_CONSENT_APPROVED_VALUE } from "@/constants/common";
import { FCM_USER_LOGIN_EVENT_NAME } from "@/hooks/useNativeBridge";

export const loginApi = async (payload: TLoginReqDto): Promise<TLoginResDto> => {
  const rs = await clientApi.post<TLoginResDto>("/login", payload);

  const { data } = rs.data;
  logout();
  if (!data.isFirstLogin) {
    localStorage.setItem(PRIVACY_CONSENT_APPROVED_KEY, PRIVACY_CONSENT_APPROVED_VALUE);
  }
  setToken(data.accessToken, data.refreshToken);
  Cookies.set("learner-account-id", data.accountId, COOKIE_OPTIONS);
  Cookies.set("learner-user-id", data.userId, COOKIE_OPTIONS);
  Cookies.set("learner-id", data.learnerId, COOKIE_OPTIONS);
  Cookies.set("learner-full-name", data.fullName, COOKIE_OPTIONS);
  Cookies.set("learner-industry-id", data.industryId, COOKIE_OPTIONS);

  // Notify useNativeBridge to register cached firebase token
  window.dispatchEvent(new Event(FCM_USER_LOGIN_EVENT_NAME));

  return rs.data;
};

export const checkWebAccessApi = async (payload: TCheckWebAccessReqDto): Promise<TCheckWebAccessRes> => {
  const response = await clientApi.post<TCheckWebAccessResDto>("/check-web-access", payload);
  return response.data.data;
};

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
  });
};

// Shared promise to prevent concurrent refresh calls (race condition between interceptor, withAuth, etc.)
let activeRefreshPromise: Promise<TRefreshTokenResDto> | null = null;

export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  // Reuse in-flight refresh request if one exists
  if (activeRefreshPromise) return activeRefreshPromise;

  activeRefreshPromise = (async () => {
    try {
      // Use refreshClient (clean axios instance — no auth header, no interceptors)
      const rs = await refreshClient.post<TRefreshTokenResDto>("/refresh-token", payload);
      const { data } = rs.data;
      // Cognito REFRESH_TOKEN_AUTH may not return a new refreshToken — keep old one
      const newRefreshToken = data.refreshToken || payload.refreshToken;
      setToken(data.accessToken, newRefreshToken);
      return rs.data;
    } finally {
      activeRefreshPromise = null;
    }
  })();

  return activeRefreshPromise;
};

export const setPasswordApi = async (payload: TSetPasswordReqDto): Promise<TSetPasswordResDto> => {
  const { data } = await clientApi.post<TSetPasswordResDto>("/set-password", payload);
  return data;
};

export const resetPasswordApi = async (payload: TResetPasswordReqDto): Promise<TResetPasswordResDto> => {
  const { data } = await clientApi.post<TResetPasswordResDto>("/reset-password", payload);
  return data;
};

export const sendEmailToChangeEmailApi = async (payload: TSendEmailToChangeUserEmailReqDto): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/send-email-to-change-user-email", payload);
  return data;
};

export const updateUserEmailApi = async (payload: TUpdateUserEmailReqDto): Promise<TUpdateUserEmailResDto> => {
  const { data } = await clientApi.post<TUpdateUserEmailResDto>("/update-user-email", payload);
  return data;
};

export const logoutApi = async (): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout");
  return rs?.data;
};

export const validateNewEmailExistsApi = async (payload: TValidateNewEmailReqDto): Promise<TValidateNewEmailResDto> => {
  const { data } = await clientApi.post<TValidateNewEmailResDto>("/validate-new-email-exists", payload);
  return data;
};

export const validateTokenApi = async (payload: TValidateTokenReqDto): Promise<TValidateTokenRes> => {
  const response = await clientApi.post<TValidateTokenResDto>("/validate-token", payload);
  return response.data.data;
};
